/**
 * Rig Tabs Component - Multi-rig management UI
 * 
 * Displays tabs for each rig, allows creating new rigs (max 5),
 * renaming rigs, and deleting rigs.
 */

import React, { useState } from 'react';
import { Tabs, IconButton, Dialog, Flex, TextField, Button, Text, Box } from '@radix-ui/themes';
import { PlusIcon, Pencil1Icon, TrashIcon, Cross2Icon } from '@radix-ui/react-icons';
import { useRigs, Rig } from '../../contexts/RigContext';

interface RigTabsProps {
  children: React.ReactNode;
}

const RigTabs: React.FC<RigTabsProps> = ({ children }) => {
  const { rigs, activeRigId, setActiveRig, createRig, updateRigName, deleteRig, canCreateRig } = useRigs();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [newRigName, setNewRigName] = useState('');
  const [renamingRigId, setRenamingRigId] = useState<string | null>(null);

  const handleCreateRig = () => {
    if (newRigName.trim()) {
      createRig(newRigName);
      setNewRigName('');
      setShowCreateDialog(false);
    }
  };

  const handleRenameRig = () => {
    if (renamingRigId && newRigName.trim()) {
      updateRigName(renamingRigId, newRigName);
      setNewRigName('');
      setRenamingRigId(null);
      setShowRenameDialog(false);
    }
  };

  const handleDeleteRig = (rigId: string, rigName: string) => {
    if (window.confirm(`Delete "${rigName}"? This will remove all devices in this rig.`)) {
      deleteRig(rigId);
    }
  };

  const openRenameDialog = (rigId: string, currentName: string) => {
    setRenamingRigId(rigId);
    setNewRigName(currentName);
    setShowRenameDialog(true);
  };

  // If no rigs exist, show welcome message
  if (rigs.length === 0) {
    return (
      <Box p="6" style={{ textAlign: 'center' }}>
        <Text size="4" weight="bold" mb="2" style={{ display: 'block' }}>
          Welcome to AstroPower Manager
        </Text>
        <Text size="2" color="gray" mb="4" style={{ display: 'block' }}>
          Create your first rig to get started
        </Text>
        <Button size="3" onClick={() => setShowCreateDialog(true)}>
          <PlusIcon /> Create First Rig
        </Button>

        {/* Create Dialog */}
        <Dialog.Root open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <Dialog.Content style={{ maxWidth: 450 }}>
            <Dialog.Title>Create New Rig</Dialog.Title>
            <Dialog.Description size="2" mb="4">
              Give your rig a name to help identify it
            </Dialog.Description>

            <Flex direction="column" gap="3">
              <label>
                <Text as="div" size="2" mb="1" weight="bold">
                  Rig Name
                </Text>
                <TextField.Root
                  value={newRigName}
                  onChange={(e) => setNewRigName(e.target.value)}
                  placeholder="e.g., Main Observatory, Travel Rig..."
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateRig()}
                  autoFocus
                />
              </label>
            </Flex>

            <Flex gap="3" mt="4" justify="end">
              <Dialog.Close>
                <Button variant="soft" color="gray">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button onClick={handleCreateRig} disabled={!newRigName.trim()}>
                Create Rig
              </Button>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>
      </Box>
    );
  }

  return (
    <>
      <Tabs.Root value={activeRigId || ''} onValueChange={setActiveRig}>
        <Tabs.List style={{ marginBottom: '16px' }}>
            {rigs.map((rig: Rig) => (
              <Tabs.Trigger key={rig.id} value={rig.id}>
                <Flex align="center" gap="2">
                  {rig.name}
                  <Flex gap="1" style={{ marginLeft: '4px' }}>
                    <IconButton
                      size="1"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        openRenameDialog(rig.id, rig.name);
                      }}
                      title="Rename rig"
                    >
                      <Pencil1Icon />
                    </IconButton>
                    {rigs.length > 1 && (
                      <IconButton
                        size="1"
                        variant="ghost"
                        color="red"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteRig(rig.id, rig.name);
                        }}
                        title="Delete rig"
                      >
                        <TrashIcon />
                      </IconButton>
                    )}
                  </Flex>
                </Flex>
              </Tabs.Trigger>
            ))}
            
            {canCreateRig && (
              <Tabs.Trigger
                value="__create__"
                onClick={() => setShowCreateDialog(true)}
                style={{ color: 'var(--violet-9)' }}
              >
                <PlusIcon /> New Rig
              </Tabs.Trigger>
            )}
        </Tabs.List>

        {rigs.map((rig: Rig) => (
          <Tabs.Content key={rig.id} value={rig.id}>
            {children}
          </Tabs.Content>
        ))}
      </Tabs.Root>

      {/* Create Rig Dialog */}
      <Dialog.Root open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <Dialog.Content style={{ maxWidth: 450 }}>
          <Dialog.Title>Create New Rig</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            You can create up to 5 rigs. Give your new rig a descriptive name.
          </Dialog.Description>

          <Flex direction="column" gap="3">
            <label>
              <Text as="div" size="2" mb="1" weight="bold">
                Rig Name
              </Text>
              <TextField.Root
                value={newRigName}
                onChange={(e) => setNewRigName(e.target.value)}
                placeholder="e.g., Main Observatory, Travel Rig..."
                onKeyDown={(e) => e.key === 'Enter' && handleCreateRig()}
                autoFocus
              />
            </label>
          </Flex>

          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button onClick={handleCreateRig} disabled={!newRigName.trim()}>
              Create Rig
            </Button>
          </Flex>

          <Dialog.Close>
            <IconButton
              style={{ position: 'absolute', top: '12px', right: '12px' }}
              variant="ghost"
              color="gray"
            >
              <Cross2Icon />
            </IconButton>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Root>

      {/* Rename Rig Dialog */}
      <Dialog.Root open={showRenameDialog} onOpenChange={setShowRenameDialog}>
        <Dialog.Content style={{ maxWidth: 450 }}>
          <Dialog.Title>Rename Rig</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            Enter a new name for this rig
          </Dialog.Description>

          <Flex direction="column" gap="3">
            <label>
              <Text as="div" size="2" mb="1" weight="bold">
                Rig Name
              </Text>
              <TextField.Root
                value={newRigName}
                onChange={(e) => setNewRigName(e.target.value)}
                placeholder="Enter rig name..."
                onKeyDown={(e) => e.key === 'Enter' && handleRenameRig()}
                autoFocus
              />
            </label>
          </Flex>

          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button onClick={handleRenameRig} disabled={!newRigName.trim()}>
              Rename
            </Button>
          </Flex>

          <Dialog.Close>
            <IconButton
              style={{ position: 'absolute', top: '12px', right: '12px' }}
              variant="ghost"
              color="gray"
            >
              <Cross2Icon />
            </IconButton>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
};

export default RigTabs;
